import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement,
  Title, 
  Tooltip, 
  Legend 
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { 
  UserGroupIcon, 
  GlobeAltIcon, 
  ChatBubbleLeftRightIcon,
  CursorArrowRaysIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  SunIcon,
  MoonIcon,
  SparklesIcon,
  HomeIcon
} from '@heroicons/react/24/outline';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../services/api';
import toast from 'react-hot-toast';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const { isAuthenticated, login, loading: authLoading } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  
  const [stats, setStats] = useState({
    totalAccounts: 0,
    activeAccounts: 0,
    totalCTAs: 0,
    totalResponses: 0,
    responsesToday: 0,
    totalClicks: 0,
    conversionRate: 0
  });
  
  const [chartData, setChartData] = useState({
    responses: {
      labels: [],
      datasets: []
    },
    conversions: {
      labels: [],
      datasets: []
    }
  });
  
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasDataRef = useRef(false); // Stale-while-revalidate: show previous data while refetching
  const { socket } = useSocket();

  // Define functions before useEffect (must be at top level)
  const fetchDashboardData = async () => {
    try {
      // Only show loading on initial load; keep showing previous data while revalidating
      setLoading(prev => (hasDataRef.current ? false : true));
      
      // Fetch dashboard statistics (backend caches these 30–60s)
      const [statsResponse, chartResponse, activityResponse] = await Promise.all([
        api.get('/analytics/dashboard'),
        api.get('/analytics/charts'),
        api.get('/analytics/recent-activity')
      ]);
      
      setStats(statsResponse.data);
      setChartData(chartResponse.data);
      // Limit to latest 5 activities
      setRecentActivity(activityResponse.data.slice(0, 5));
      hasDataRef.current = true;
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Batched message-sent handler: accumulate messages and flush every 500ms
  const pendingMessagesRef = useRef([]);
  const pendingAutomationDeltaRef = useRef(0);
  const flushTimerRef = useRef(null);

  const flushPendingUpdates = useCallback(() => {
    // Flush automation delta
    const delta = pendingAutomationDeltaRef.current;
    if (delta !== 0) {
      pendingAutomationDeltaRef.current = 0;
      setStats(prev => ({
        ...prev,
        activeAccounts: Math.max(0, prev.activeAccounts + delta)
      }));
    }

    // Flush pending messages
    const messages = pendingMessagesRef.current;
    if (messages.length > 0) {
      pendingMessagesRef.current = [];
      const count = messages.length;
      
      setStats(prev => ({
        ...prev,
        totalResponses: prev.totalResponses + count,
        responsesToday: prev.responsesToday + count
      }));

      // Only keep the latest message for recent activity (avoid 800 items)
      const latestMsg = messages[messages.length - 1];
      setRecentActivity(prev => [
        {
          id: Date.now(),
          type: 'message_sent',
          account: latestMsg.accountId,
          message: latestMsg.response,
          timestamp: latestMsg.timestamp
        },
        ...prev.slice(0, 4)
      ]);
    }
  }, []);

  const scheduleFlush = useCallback(() => {
    if (!flushTimerRef.current) {
      flushTimerRef.current = setTimeout(() => {
        flushTimerRef.current = null;
        flushPendingUpdates();
      }, 500);
    }
  }, [flushPendingUpdates]);

  const handleAutomationUpdate = useCallback((data) => {
    pendingAutomationDeltaRef.current += (data.status === 'running' ? 1 : -1);
    scheduleFlush();
  }, [scheduleFlush]);

  const handleMessageSent = useCallback((data) => {
    pendingMessagesRef.current.push(data);
    scheduleFlush();
  }, [scheduleFlush]);

  // Authenticated - show dashboard (useEffect must be at top level)
  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboardData();
      
      // Listen for real-time updates
      if (socket) {
        socket.on('automation-started', handleAutomationUpdate);
        socket.on('automation-stopped', handleAutomationUpdate);
        socket.on('message-sent', handleMessageSent);
        
        return () => {
          socket.off('automation-started');
          socket.off('automation-stopped');
          socket.off('message-sent');
          // Clear pending flush on cleanup
          if (flushTimerRef.current) {
            clearTimeout(flushTimerRef.current);
            flushTimerRef.current = null;
          }
        };
      }
    }
  }, [socket, isAuthenticated, handleAutomationUpdate, handleMessageSent]);

  // Show login form if not authenticated
  if (!authLoading && !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, backgroundColor: 'inherit' }}>
        {/* Dark mode toggle button */}
        <button
          onClick={toggleTheme}
          className="absolute top-4 right-4 p-2 rounded-xl text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
          title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{ position: 'absolute', zIndex: 10001 }}
        >
          {isDarkMode ? (
            <SunIcon className="h-6 w-6" />
          ) : (
            <MoonIcon className="h-6 w-6" />
          )}
        </button>
        
        <div className="max-w-md w-full space-y-8" style={{ position: 'relative', zIndex: 10000 }}>
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
              X DM Automation
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
              Sign in to access your dashboard
            </p>
          </div>
          <form 
            className="mt-8 space-y-6" 
            onSubmit={async (e) => {
              e.preventDefault();
              setLoginLoading(true);
              try {
                const result = await login(loginForm.username, loginForm.password);
                if (result.success) {
                  toast.success('Login successful!');
                } else {
                  toast.error(result.error || 'Invalid username / password');
                }
              } catch (error) {
                toast.error('Invalid username / password');
              } finally {
                setLoginLoading(false);
              }
            }}
          >
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="username" className="sr-only">
                  Username
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="Username"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                  style={{ pointerEvents: 'auto' }}
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="Password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  style={{ pointerEvents: 'auto' }}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loginLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                style={{ 
                  pointerEvents: 'auto', 
                  cursor: loginLoading ? 'wait' : 'pointer',
                  background: 'linear-gradient(to right, #2563eb, #0284c7)'
                }}
                onMouseEnter={(e) => {
                  if (!loginLoading) {
                    e.target.style.background = 'linear-gradient(to right, #3b82f6, #0ea5e9)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loginLoading) {
                    e.target.style.background = 'linear-gradient(to right, #2563eb, #0284c7)';
                  }
                }}
              >
                {loginLoading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, change, changeType = 'neutral' }) => (
    <div className="card">
      <div className="card-body">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Icon className="h-8 w-8" style={{ color: '#4da0fe' }} />
          </div>
          <div className="ml-4 flex-1">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
            <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{value}</p>
            {change && (
              <div className="flex items-center mt-1">
                {changeType === 'positive' ? (
                  <ArrowTrendingUpIcon className="h-4 w-4 text-success-500" />
                ) : changeType === 'negative' ? (
                  <ArrowTrendingDownIcon className="h-4 w-4 text-danger-500" />
                ) : null}
                <span className={`text-sm ml-1 ${
                  changeType === 'positive' ? 'text-success-600 dark:text-success-400' : 
                  changeType === 'negative' ? 'text-danger-600 dark:text-danger-400' : 
                  'text-gray-600 dark:text-gray-400'
                }`}>
                  {change}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner h-8 w-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div className={`relative overflow-hidden rounded-xl ${
        isDarkMode 
          ? 'bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900' 
          : 'bg-gradient-to-br from-sky-50 via-blue-100 to-sky-100'
      }`}>
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient orbs */}
          <div className={`absolute -top-20 -left-20 w-80 h-80 rounded-full blur-3xl ${
            isDarkMode ? 'bg-blue-600/20' : 'bg-blue-300/40'
          }`} />
          <div className={`absolute -bottom-24 -right-24 w-96 h-96 rounded-full blur-3xl ${
            isDarkMode ? 'bg-sky-600/15' : 'bg-sky-300/30'
          }`} />
          <div className={`absolute top-1/2 right-1/3 -translate-y-1/2 w-64 h-64 rounded-full blur-3xl ${
            isDarkMode ? 'bg-cyan-600/10' : 'bg-cyan-200/30'
          }`} />
          
          {/* Grid pattern overlay */}
          <div 
            className={`absolute inset-0 ${isDarkMode ? 'opacity-[0.03]' : 'opacity-[0.08]'}`}
            style={{
              backgroundImage: `linear-gradient(${isDarkMode ? '#fff' : '#3b82f6'} 1px, transparent 1px), linear-gradient(90deg, ${isDarkMode ? '#fff' : '#3b82f6'} 1px, transparent 1px)`,
              backgroundSize: '40px 40px'
            }}
          />
          
          {/* Floating decorative shapes */}
          <svg className="absolute top-6 right-8 w-24 h-24 opacity-10" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="45" fill="none" stroke={isDarkMode ? '#93c5fd' : '#3b82f6'} strokeWidth="1.5" />
            <circle cx="50" cy="50" r="30" fill="none" stroke={isDarkMode ? '#93c5fd' : '#3b82f6'} strokeWidth="1" />
            <circle cx="50" cy="50" r="15" fill="none" stroke={isDarkMode ? '#93c5fd' : '#3b82f6'} strokeWidth="0.5" />
          </svg>
          <svg className="absolute bottom-8 left-1/4 w-14 h-14 opacity-10" viewBox="0 0 100 100">
            <rect x="15" y="15" width="70" height="70" rx="10" fill="none" stroke={isDarkMode ? '#7dd3fc' : '#0284c7'} strokeWidth="2" transform="rotate(12 50 50)" />
          </svg>
        </div>
        
        {/* Content */}
        <div className="relative px-8 py-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              {/* Icon/Logo area */}
              <div className={`relative flex items-center justify-center w-16 h-16 rounded-2xl ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-blue-500 to-sky-600 shadow-lg shadow-blue-500/30' 
                  : 'bg-gradient-to-br from-blue-500 to-sky-500 shadow-lg shadow-blue-400/40'
              }`}>
                <HomeIcon className="w-8 h-8 text-white" />
                <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center ${
                  isDarkMode ? 'bg-cyan-400' : 'bg-cyan-500'
                }`}>
                  <SparklesIcon className="w-3 h-3 text-white" />
                </div>
              </div>
              
              {/* Text content */}
              <div>
                <h1 className={`text-3xl font-bold tracking-tight ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}>
                  Dashboard
                </h1>
                <p className={`mt-1.5 text-base ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}>
                  Overview of your X DM automation system
                </p>
                <div className={`mt-3 flex items-center space-x-4 text-sm ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}>
                  <span className="flex items-center">
                    <span className={`w-2 h-2 rounded-full mr-2 ${
                      isDarkMode ? 'bg-emerald-400' : 'bg-emerald-500'
                    }`} />
                    {stats.activeAccounts} Active
                  </span>
                  <span className="flex items-center">
                    <span className={`w-2 h-2 rounded-full mr-2 ${
                      isDarkMode ? 'bg-slate-500' : 'bg-slate-400'
                    }`} />
                    {stats.totalAccounts} Total Accounts
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Accounts"
          value={stats.totalAccounts}
          icon={UserGroupIcon}
        />
        <StatCard
          title="Active Automations"
          value={stats.activeAccounts}
          icon={ChatBubbleLeftRightIcon}
          change={stats.activeAccounts > 0 ? "Running" : "Stopped"}
          changeType={stats.activeAccounts > 0 ? "positive" : "neutral"}
        />
        <StatCard
          title="Total CTAs Shared"
          value={stats.totalCTAs}
          icon={GlobeAltIcon}
        />
        <StatCard
          title="Responses Today"
          value={stats.responsesToday}
          icon={CursorArrowRaysIcon}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Responses Chart */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Response Trends</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Daily response count over time</p>
          </div>
          <div className="card-body overflow-hidden">
            <Line data={chartData.responses} options={chartOptions} />
          </div>
        </div>

        {/* Conversions Chart */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Conversion Rate</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Click-through rate over time</p>
          </div>
          <div className="card-body overflow-hidden">
            <Bar data={chartData.conversions} options={chartOptions} />
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Recent Activity</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Latest automation events</p>
        </div>
        <div className="card-body p-0">
          <div className="overflow-hidden">
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {recentActivity.length > 0 ? (
                recentActivity.slice(0, 5).map((activity) => (
                  <li key={activity.id} className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        {activity.type === 'message_sent' ? (
                          <div className="h-2 w-2 bg-success-500 rounded-full"></div>
                        ) : activity.type === 'automation_started' ? (
                          <div className="h-2 w-2 rounded-full" style={{ backgroundColor: '#4da0fe' }}></div>
                        ) : (
                          <div className="h-2 w-2 bg-warning-500 rounded-full"></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 dark:text-gray-100">
                          {activity.type === 'message_sent' && 'Response sent'}
                          {activity.type === 'automation_started' && 'Automation started'}
                          {activity.type === 'automation_stopped' && 'Automation stopped'}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                          {activity.message || activity.account}
                        </p>
                      </div>
                      <div className="flex-shrink-0 text-sm text-gray-500 dark:text-gray-400">
                        {new Date(activity.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                  </li>
                ))
              ) : (
                <li className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                  No recent activity
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
